import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n-composable'
import { ActionName } from '@/components/ActionsBar/types/ActionBarComponentTypes'
import PromptDialogComponent from '@/components/Dialog/PromptDialogComponent.vue'
import { useSearchIAStore } from '@/modules/documentSearcher/stores/useSearchIAStore'
import type { FilterOption, FilterType } from '@/modules/documentSearcher/types/SearchIATypes'
import { FilterTypes } from '@/modules/documentSearcher/types/SearchIATypes'
import { TrackerEvents, trackEvent } from '@/plugins/tracker'

// PROPS

export default /*#__PURE__*/_defineComponent({
  __name: 'ChatFilterComponent',
  props: {
  defaultFilter: {
    type: String,
    default: ''
  }
},
  emits: ['filter-selected'],
  setup(__props, { emit }) {

const props = __props;



// EMIT


// STORE
const searchIAStore = useSearchIAStore()

// TRANSLATIONS
const { t } = useI18n()
const TEXT = {
  documentsInContactExpedients: t('components.chat_filter.documents_in_contact_expedients') as string,
  general: t('views.search_ia.general') as string,
  generalDescription: t('views.search_ia.general_description') as string,
  labelExpedient: t('components.chat_filter.expedients') as string,
  searchIn: t('views.search_ia.search_in') as string,
  selectContact: t('views.search_ia.select_contact') as string,
  selectContactTitle: t('components.chat_filter.select_contact') as string,
  selectExpedient: t('views.search_ia.select_expedient') as string,
  selectExpedientTitle: t('components.chat_filter.select_expedient') as string,
  selectPlaceholder: t('components.chat_filter.select_placeholder') as string
}

// ICONS
const ICONS = {
  [FilterTypes.GENERAL]: require('@/assets/images/svg/icon-documents.svg'),
  [FilterTypes.EXPEDIENT]: require('@/assets/images/svg/icon-law.svg'),
  [FilterTypes.CONTACT]: require('@/assets/images/svg/icon-agenda.svg'),
  chevronDown: require('@/assets/images/svg/icon-chevron-down.svg')
}

// DATA
const selectedOption = ref<FilterOption | null>(null)
const selectedFilter = ref(props.defaultFilter)
const promptTitle = ref('')
const promptContext = ref('filterSelectionForm')
const openedPrompt = ref(false)
const currentFormSchema = ref<any[]>([])
const formData = ref<any>({})
const selectedEntityName = ref<string>('')
const isMenuOpen = ref(false)

// Helper to create filter option
const createFilterOption = (
  id: FilterType,
  label: string,
  description: string | null,
  selectedText?: string
): FilterOption => ({
  id,
  label,
  description,
  icon: ICONS[id],
  selectedText
})

// Filter options for the select
const filterOptions = computed((): FilterOption[] => [
  createFilterOption(FilterTypes.GENERAL, TEXT.general, TEXT.generalDescription),
  createFilterOption(
    FilterTypes.EXPEDIENT,
    TEXT.selectExpedient,
    null,
    selectedFilter.value === FilterTypes.EXPEDIENT ? selectedEntityName.value : undefined
  ),
  createFilterOption(
    FilterTypes.CONTACT,
    TEXT.selectContact,
    null,
    selectedFilter.value === FilterTypes.CONTACT ? selectedEntityName.value : undefined
  )
])

// Set initial selection to 'general'
selectedOption.value = filterOptions.value[0]

// Form Schemas
const parsedFormSchemaExpedients = [
  {
    id: 'expedientId',
    lg: 12,
    md: 12,
    xl: 12,
    name: 'expedientId',
    items: 'types',
    keyId: 'ref',
    label: TEXT.labelExpedient,
    keyName: 'name',
    keyValue: 'ref',
    fieldType: 'LexonSelectComponent',
    placeholder: TEXT.selectPlaceholder,
    tableHeaders: [
      {
        table: 'default',
        columns: [
          {
            name: 'Ref',
            width: 20
          },
          {
            name: 'Description',
            width: 40
          },
          {
            name: 'customerName',
            width: 40
          }
        ]
      }
    ],
    endPointEntity: {
      url: 'action/contact/EXP',
      method: 'POST',
      params: [
        {
          name: 'order',
          value: 'name'
        },
        {
          name: 'typeOrder',
          value: 'asc'
        }
      ]
    },
    validationRules: 'required|objectNotEmpty',
    serverPagination: true,
    returnObject: false,
    attach: false
  }
]

const parsedFormSchemaContacts = [
  {
    id: 'name',
    name: 'name',
    type: 'text',
    label: TEXT.documentsInContactExpedients,
    fieldType: 'LexonSelectComponent',
    loadMethod: 'contacts/all/get',
    validationRules: 'required',
    itemName: 'name',
    itemValue: 'name',
    tableHeaders: ['Name', 'Email', 'MobilePhone', 'Phone'],
    attach: false,
    xs: 12,
    sm: 12,
    md: 12,
    lg: 12,
    xl: 12
  }
]

// COMPUTED AND METHODS
const resetToGeneral = () => {
  selectedFilter.value = ''
  selectedEntityName.value = ''
  searchIAStore.resetFilter()
  selectedOption.value = filterOptions.value[0]
}

const onSelectChange = (item: FilterOption) => {
  trackEvent(TrackerEvents.IA_FILTER_APPLIED, { filter_type: item.id })
  if (item.id === FilterTypes.GENERAL) {
    resetToGeneral()
  } else {
    openFilterPrompt(item)
  }
}

const FILTER_CONFIG = {
  [FilterTypes.EXPEDIENT]: {
    title: TEXT.selectExpedientTitle,
    schema: parsedFormSchemaExpedients,
    formField: 'expedientId'
  },
  [FilterTypes.CONTACT]: {
    title: TEXT.selectContactTitle,
    schema: parsedFormSchemaContacts,
    formField: 'name'
  }
} as const

const openFilterPrompt = (item: FilterOption) => {
  selectedFilter.value = item.id
  formData.value = {}

  if (item.id === FilterTypes.EXPEDIENT || item.id === FilterTypes.CONTACT) {
    const config = FILTER_CONFIG[item.id]
    promptTitle.value = config.title
    currentFormSchema.value = config.schema
    openedPrompt.value = true
  }
}

const extractEntityName = (data: any): string => {
  if (typeof data === 'object') {
    // Si existe name, construir nombre completo con apellidos
    if (data.name) {
      const fullName = [data.name, data.firstSurname, data.secondSurname].filter(Boolean).join(' ')
      return fullName || data.name
    }
    return data.Description || data.description || data.Name || data.Ref || data.ref || ''
  }
  return String(data)
}

const handleFilterSelection = (filterType: FilterType, formFieldKey: string) => {
  const fieldData = formData.value[formFieldKey]
  if (!fieldData) return

  const entityName = extractEntityName(fieldData)
  selectedEntityName.value = entityName
  selectedOption.value = createFilterOption(filterType, entityName, null, entityName)

  const filterValue = typeof fieldData === 'object' ? fieldData.id || fieldData : fieldData
  searchIAStore.setFilter(filterType, filterValue, entityName)
}

const handleExecutePromptAction = (actionName: ActionName) => {
  if (actionName === ActionName.CLOSE) {
    openedPrompt.value = false
    resetToGeneral()
    return
  }

  if (actionName === ActionName.SAVE) {
    if (selectedFilter.value === FilterTypes.EXPEDIENT) {
      handleFilterSelection(FilterTypes.EXPEDIENT, 'expedientId')
    } else if (selectedFilter.value === FilterTypes.CONTACT) {
      handleFilterSelection(FilterTypes.CONTACT, 'name')
    }
    openedPrompt.value = false
  }
}

// Initialize from store on mount
onMounted(() => {
  if (searchIAStore.filterType && searchIAStore.filterEntityName) {
    selectedFilter.value = searchIAStore.filterType
    selectedEntityName.value = searchIAStore.filterEntityName

    // Find the option and set it
    const option = filterOptions.value.find((opt) => opt.id === searchIAStore.filterType)
    if (option) {
      selectedOption.value = {
        ...option,
        label: searchIAStore.filterEntityName,
        selectedText: searchIAStore.filterEntityName
      }
    }
  }
})

return { __sfc: true,props, emit, searchIAStore, t, TEXT, ICONS, selectedOption, selectedFilter, promptTitle, promptContext, openedPrompt, currentFormSchema, formData, selectedEntityName, isMenuOpen, createFilterOption, filterOptions, parsedFormSchemaExpedients, parsedFormSchemaContacts, resetToGeneral, onSelectChange, FILTER_CONFIG, openFilterPrompt, extractEntityName, handleFilterSelection, handleExecutePromptAction, PromptDialogComponent }
}

})